import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import AccessTime from '@mui/icons-material/AccessTime';
import CalendarToday from '@mui/icons-material/CalendarToday';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import { perkApi } from 'components/loyalty/PerkApi';
import { Perk } from 'components/loyalty/models/PerkModel';
import { LegacyForm } from 'lib/LegacyForm';
import { Option } from 'lib/types';
import { AutoForm } from 'ui/form/AutoForm';
import {
    createModuleForm,
    transformFormFieldToModuleFields,
    transformModuleToFormFields
} from '../forms/createModuleForm';
import { ContentModule, ContentModuleType, EHeightOption, IItemContext } from '../models/ContentModule';
import { Row } from 'ui/Flex';
import { NEW_MODULE_ID } from 'store/content/contentSlice';
import { RowCopy } from 'lib/RowCopy';
import { ApplicationState } from 'store/store';
import { ModuleSchedulingModal } from '../forms/module-scheduling/ModuleSchedulingModal';
import { isDefined } from 'lib/typeguards';
import { weekdays } from 'components/perks/award-enrichments/consts';
import { useAppSelector } from 'store/hooks';
import { selectMerchantTimezone } from 'store/settings/settingsSlice';

interface ModuleFormProps {
    module: ContentModule;
    moduleId: string;
    handleSubmit: () => void;
    updateCurrentModuleProperties: (fieldName: string, value: any, id: string) => void;
    onDelete: (id: string) => void;
}

const schedulePaperSx = { p: 2 };

const timeOfDayFormatter = new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit'
});

const formatTimeOfDay = (seconds: number): string => {
    const date = new Date();
    date.setHours(Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), 0, 0);
    return timeOfDayFormatter.format(date);
};

const visibleRangeFormatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
});

const formatVisibleRange = (visibleFrom?: string, visibleTo?: string): string => {
    const from = visibleFrom ? visibleRangeFormatter.format(new Date(visibleFrom)) : '';
    const to = visibleTo ? visibleRangeFormatter.format(new Date(visibleTo)) : '';
    if (from && to) {
        return `${from} → ${to}`;
    }
    return from || to;
};

const moduleTypeToTitleMap: Record<ContentModuleType, string> = {
    [ContentModuleType.PRIMARY_ACTION]: 'Primary Action',
    [ContentModuleType.MARKETING]: 'Marketing',
    [ContentModuleType.LOCATIONS]: 'Locations',
    [ContentModuleType.FAVOURITES]: 'Favourites',
    [ContentModuleType.AWARDS]: 'Awards',
    [ContentModuleType.LOYALTY_BALANCE]: 'Loyalty Balance',
    [ContentModuleType.SECRET_DJ]: 'Secret DJ'
};

export const ModuleForm: React.FunctionComponent<ModuleFormProps> = ({
    module,
    moduleId,
    onDelete,
    handleSubmit,
    updateCurrentModuleProperties
}) => {
    const [display, setDisplay] = React.useState(module.display);
    const [formData, setFormData] = React.useState(null);
    const [perkOptions, setPerkOptions] = React.useState<Option[]>([]);
    const [perksLoaded, setPerksLoaded] = React.useState(module.type !== ContentModuleType.AWARDS);
    const [schedulingFormOpen, setSchedulingFormOpen] = React.useState(false);

    // Redux selectors for locations and audiences
    const { locations: reduxLocations, isLoading: locationsLoading } = useSelector(
        (state: ApplicationState) => state.locations
    );
    const { audiences: reduxAudiences, isLoading: audiencesLoading } = useSelector(
        (state: ApplicationState) => state.audiences
    );
    const merchantTimezone = useAppSelector(selectMerchantTimezone);

    // Load perks for Awards module
    React.useEffect(() => {
        if (module.type === ContentModuleType.AWARDS) {
            perkApi
                .getAllPointPerks()
                .then(response => {
                    if (response.ok && response.body?.items) {
                        const options: Option[] = response.body.items.map((perk: Perk) => ({
                            value: perk._id,
                            label: perk.internalTitle || perk.title
                        }));
                        setPerkOptions(options);
                    }
                })
                .finally(() => {
                    setPerksLoaded(true);
                });
        }
    }, [module.type]);

    // Map Redux data to options
    const locationOptions = React.useMemo(
        () => reduxLocations?.map(loc => ({ value: loc._id, label: loc.title })) ?? [],
        [reduxLocations]
    );
    const segmentOptions = React.useMemo(
        () => reduxAudiences?.map(seg => ({ value: seg._id, label: seg.title })) ?? [],
        [reduxAudiences]
    );

    // Determine loading state based on module type
    const locationsReady = module.type !== ContentModuleType.MARKETING || !locationsLoading;
    const segmentsReady = module.type !== ContentModuleType.MARKETING || !audiencesLoading;

    const isReady = React.useMemo(
        () => !!createModuleForm(module.type) && perksLoaded && locationsReady && segmentsReady,
        [module.type, perksLoaded, locationsReady, segmentsReady]
    );

    const transformedFields = React.useMemo(
        () =>
            transformModuleToFormFields(module, {
                display,
                perkOptions,
                locationOptions,
                segmentOptions
            }),
        [module, display, perkOptions, locationOptions, segmentOptions]
    );

    const selectedSegmentIds = React.useMemo(
        () => transformedFields?.segmentIds ?? [],
        [transformedFields?.segmentIds]
    );
    const selectedExcludingSegmentIds = React.useMemo(
        () => transformedFields?.excludingSegmentIds ?? [],
        [transformedFields?.excludingSegmentIds]
    );
    const selectedIncludePerkIds = React.useMemo(
        () => transformedFields?.includePerkIds ?? [],
        [transformedFields?.includePerkIds]
    );
    const selectedExcludePerkIds = React.useMemo(
        () => transformedFields?.excludePerkIds ?? [],
        [transformedFields?.excludePerkIds]
    );

    const additionalData = React.useMemo(
        () => ({
            display,
            perkOptions,
            locationOptions,
            segmentOptions,
            selectedSegmentIds,
            selectedExcludingSegmentIds,
            selectedIncludePerkIds,
            selectedExcludePerkIds
        }),
        [
            display,
            perkOptions,
            locationOptions,
            segmentOptions,
            selectedSegmentIds,
            selectedExcludingSegmentIds,
            selectedIncludePerkIds,
            selectedExcludePerkIds
        ]
    );
    const form = React.useMemo(
        () =>
            new LegacyForm(createModuleForm(module.type, additionalData), handleSubmit).update({
                ...transformedFields
            }),
        [module.type, additionalData, handleSubmit, transformedFields]
    );

    const handleBlur = React.useCallback(
        (name: string, value: any) => {
            const updatedFormData = { ...formData, [name]: value };

            setFormData(updatedFormData);

            if (name === 'display') {
                setDisplay({ height: value });
            }

            const transformedFields = transformFormFieldToModuleFields(module, name, value, updatedFormData);

            transformedFields.forEach(({ name, value }) =>
                updateCurrentModuleProperties(name, value, moduleId)
            );
        },
        [formData, module, moduleId, updateCurrentModuleProperties]
    );

    const handleOpenSchedule = React.useCallback(() => {
        setSchedulingFormOpen(true);
    }, []);

    const handleCloseSchedule = React.useCallback(() => {
        setSchedulingFormOpen(false);
    }, []);

    const handleDeleteSchedule = React.useCallback(() => {
        handleBlur('context', {
            ...module.context,
            schedule: undefined,
            visibleFrom: undefined,
            visibleTo: undefined
        });
    }, [handleBlur, module.context]);

    const handleSubmitSchedule = React.useCallback(
        (values: Pick<IItemContext, 'visibleTo' | 'visibleFrom' | 'schedule'>) => {
            setSchedulingFormOpen(false);
            handleBlur('context', { ...module.context, ...values });
        },
        [handleBlur, module.context]
    );

    const schedulingFormData = React.useMemo(
        () => ({
            visibleTo: module.context?.visibleTo,
            visibleFrom: module.context?.visibleFrom,
            schedule: module.context?.schedule
        }),
        [module.context]
    );

    const hasSchedule = React.useMemo(
        () =>
            (isDefined(schedulingFormData.schedule) && schedulingFormData.schedule.length > 0) ||
            schedulingFormData.visibleFrom ||
            schedulingFormData.visibleTo,
        [schedulingFormData.schedule, schedulingFormData.visibleFrom, schedulingFormData.visibleTo]
    );

    const hasDailySchedule = React.useMemo(
        () => isDefined(schedulingFormData.schedule) && schedulingFormData.schedule.length > 0,
        [schedulingFormData.schedule]
    );

    React.useEffect(() => {
        setDisplay(module.display);
    }, [module.display]);

    const handleDelete = React.useCallback(() => {
        onDelete(moduleId);
    }, [onDelete, moduleId]);

    const buttonDisabled = React.useMemo(() => {
        const formData = form.getFormData();
        if (module.type === ContentModuleType.MARKETING && !!display) {
            if (display.height === EHeightOption.LARGE_SQUARE) {
                return formData.items?.length < 2;
            }

            if (display?.height === EHeightOption.SMALL_SQUARE) {
                return formData.items?.length < 3;
            }
        }
    }, [display, form, module.type]);

    const renderButtons = React.useCallback(
        (submitButton: React.ReactElement) => (
            <Row align="space-between">
                <Row>
                    <Button variant="outlined" color="error" onClick={handleDelete}>
                        Delete Module
                    </Button>
                </Row>
                {submitButton}
            </Row>
        ),
        [handleDelete]
    );

    if (!isReady) {
        return (
            <Box padding={2} display="flex" alignItems="center" gap={2}>
                <CircularProgress size={20} />
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box padding={2}>
            <AutoForm
                buttonDisabled={buttonDisabled}
                onBlur={handleBlur}
                form={form}
                submitLabel="Save"
                renderButtons={renderButtons}
                title={`Configure your ${moduleTypeToTitleMap[module.type]} module`}
            >
                {(fields, render) => (
                    <>
                        {!!module.id && module.id !== NEW_MODULE_ID && (
                            <Grid container spacing={2} ml={0}>
                                <Grid
                                    size={{
                                        xl: 12,
                                        xs: 12
                                    }}
                                >
                                    <RowCopy value={module.id} text="ID" variant="subtitle2" />
                                </Grid>
                            </Grid>
                        )}
                        {render(fields)}
                        <Grid container spacing={2} ml={0} width="100%">
                            {hasSchedule && (
                                <Grid size={12}>
                                    <Paper variant="outlined" sx={schedulePaperSx}>
                                        <Stack spacing={1.5}>
                                            <Row valign="center" align="space-between">
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <AccessTime fontSize="small" />
                                                    <Typography variant="subtitle1">Schedule</Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <IconButton size="small" onClick={handleOpenSchedule}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={handleDeleteSchedule}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Row>
                                            {hasDailySchedule && (
                                                <Stack spacing={0.5}>
                                                    {weekdays.map(day => {
                                                        const entry = schedulingFormData.schedule?.find(
                                                            s => s.daysOfWeek === Number(day.value)
                                                        );
                                                        return (
                                                            <Row key={day.value} valign="center">
                                                                <Box width={140}>
                                                                    <Typography variant="body2">
                                                                        {day.label}
                                                                    </Typography>
                                                                </Box>
                                                                {entry && (
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.tertiary"
                                                                    >
                                                                        {formatTimeOfDay(entry.timeFrom)} →
                                                                        {formatTimeOfDay(entry.timeTo)}
                                                                    </Typography>
                                                                )}
                                                                {!entry && (
                                                                    <Typography variant="caption">
                                                                        Unavailable
                                                                    </Typography>
                                                                )}
                                                            </Row>
                                                        );
                                                    })}
                                                </Stack>
                                            )}

                                            {(schedulingFormData.visibleFrom ||
                                                schedulingFormData.visibleTo) && (
                                                <Row valign="center" gutter>
                                                    <CalendarToday fontSize="small" />
                                                    <Typography variant="body2">
                                                        {formatVisibleRange(
                                                            schedulingFormData.visibleFrom,
                                                            schedulingFormData.visibleTo
                                                        )}
                                                    </Typography>
                                                </Row>
                                            )}
                                        </Stack>
                                    </Paper>
                                </Grid>
                            )}
                            {!hasSchedule && (
                                <Grid size={4}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="primary"
                                        fullWidth
                                        onClick={handleOpenSchedule}
                                    >
                                        Add Schedule
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                        <ModuleSchedulingModal
                            existingSchedule={schedulingFormData}
                            merchantTimezone={merchantTimezone}
                            open={schedulingFormOpen}
                            onClose={handleCloseSchedule}
                            onSubmit={handleSubmitSchedule}
                        />
                    </>
                )}
            </AutoForm>
        </Box>
    );
};
